/**
 * Multiplayer Game Component - Active alkagi game with event sourcing.
 *
 * Active-game layout mirrors the playground/warchest pattern: top bar
 * (leave · active-player chip · score chips · turn badge · connection pill)
 * over a wood-tone board frame, with the info side-panel below. Mobile keeps
 * the existing portrait stack so the canvas drag area can dominate the screen.
 */

import type {
  EntityBody,
  KineticCoreState,
} from "@cloudnest/kinetic-core-engine";
import { useCallback, useEffect, useRef } from "react";
import { buildLobbyPath, navigateTo } from "../../router";
import { useGameEventsWS } from "../../shared/hooks/use-game-events-ws";
import { getUserLabel, useAuth } from "../../shared/store/auth-store";
import { useLobby, useLobbyActions } from "../../shared/store/lobby-store";
import { GameCanvas } from "./GameCanvas";
import { GameInfo } from "./GameInfo";
import { GameOverScreen } from "./GameOverScreen";
import { useCanvasSize } from "./hooks/use-canvas-size";
import {
  multiplayerGameActions,
  useMultiplayerGame,
  useMultiplayerGameActions,
} from "./multiplayer-game-store";
import { resolveAlkagiRouteMode } from "./route-mode";

type SeatLabel = "white" | "black";

function isHost(participantIndex: number): boolean {
  return participantIndex === 0;
}

function seatFor(participantIndex: number): SeatLabel {
  return isHost(participantIndex) ? "white" : "black";
}

function seatTokenColor(seat: SeatLabel): string {
  return seat === "white"
    ? "var(--cn-alkagi-stone-white)"
    : "var(--cn-alkagi-stone-black)";
}

function seatTextColor(seat: SeatLabel): string {
  return seat === "white"
    ? "var(--cn-alkagi-stone-black)"
    : "var(--cn-alkagi-stone-white)";
}

function seatLabel(seat: SeatLabel): string {
  return seat === "white" ? "White" : "Black";
}

function countRemaining(
  entities: ReadonlyArray<EntityBody>,
  owner: string,
): number {
  return entities.filter(
    (entity) => !entity.eliminated && entity.owner === owner,
  ).length;
}

function ConnectionPill({
  connectionStatus,
  userLabel,
  roleLabel,
}: {
  readonly connectionStatus: "connected" | "reconnecting";
  readonly userLabel: string;
  readonly roleLabel: string;
}) {
  return (
    <div
      className="flex items-center gap-1.5 rounded-full px-2.5 py-1"
      style={{
        background: "var(--cn-alkagi-surface-secondary)",
        border: "1px solid var(--cn-alkagi-surface-border)",
      }}
      data-testid="alkagi-connection-pill"
    >
      <span
        className="h-2 w-2 rounded-full"
        style={{
          background:
            connectionStatus === "connected"
              ? "var(--cn-alkagi-faction-han)"
              : "var(--cn-alkagi-aim-dot)",
        }}
      />
      <span
        className="text-xs"
        style={{ color: "var(--cn-alkagi-text-muted)" }}
      >
        {userLabel ? `${userLabel} · ` : ""}
        {roleLabel}
        {connectionStatus === "reconnecting" ? " · 재연결 중..." : ""}
      </span>
    </div>
  );
}

function ActivePlayerChip({
  seat,
  isMyTurn,
}: {
  readonly seat: SeatLabel;
  readonly isMyTurn: boolean;
}) {
  return (
    <div
      className="flex items-center gap-1.5 rounded-full px-3 py-1.5"
      style={{
        background: seatTokenColor(seat),
        color: seatTextColor(seat),
        border:
          seat === "white"
            ? "1px solid var(--cn-alkagi-stone-white-border)"
            : "1px solid transparent",
      }}
      data-testid="alkagi-active-player"
    >
      <span
        className="h-2 w-2 rounded-full"
        style={{ background: seatTextColor(seat) }}
      />
      <span className="font-semibold text-sm">
        {seatLabel(seat)}
        {isMyTurn ? " · 내 차례" : " · 상대 차례"}
      </span>
    </div>
  );
}

function ScoreChip({
  seat,
  count,
  isActive,
}: {
  readonly seat: SeatLabel;
  readonly count: number;
  readonly isActive: boolean;
}) {
  return (
    <div
      className="flex items-center gap-1.5 rounded-full px-3 py-1"
      style={{
        background: seatTokenColor(seat),
        color: seatTextColor(seat),
        border:
          seat === "white"
            ? "1px solid var(--cn-alkagi-stone-white-border)"
            : "1px solid transparent",
        boxShadow: isActive
          ? "0 0 0 2px var(--cn-alkagi-stone-selected)"
          : "none",
      }}
      data-testid={`alkagi-stone-count-${seat}`}
    >
      <span className="font-bold text-sm">{seatLabel(seat)}</span>
      <span className="font-mono text-xs">{count}</span>
    </div>
  );
}

function StoneCounterRow({
  seat,
  count,
}: {
  readonly seat: SeatLabel;
  readonly count: number;
}) {
  const discs = Array.from({ length: count }, (_, idx) => idx);
  const fill = seatTokenColor(seat);
  const border =
    seat === "white"
      ? "1px solid var(--cn-alkagi-stone-white-border)"
      : "1px solid transparent";
  return (
    <div
      className="flex items-center gap-1"
      data-testid={`alkagi-stone-row-${seat}`}
    >
      <span
        className="font-medium text-xs"
        style={{ color: "var(--cn-alkagi-text-muted)", minWidth: "3.5rem" }}
      >
        {seatLabel(seat)} ({count})
      </span>
      {discs.map((idx) => (
        <span
          key={`${seat}-${idx}`}
          className="inline-block"
          style={{
            width: "12px",
            height: "12px",
            borderRadius: "50%",
            background: fill,
            border,
          }}
        />
      ))}
    </div>
  );
}

function WinBanner({
  gameState,
  myParticipantId,
}: {
  readonly gameState: KineticCoreState;
  readonly myParticipantId: string | null;
}) {
  const isWin = gameState.winner === myParticipantId;
  const isDraw = gameState.isDraw;

  const seat: SeatLabel | null =
    gameState.winner === gameState.participants[0].id
      ? "white"
      : gameState.winner === gameState.participants[1]?.id
        ? "black"
        : null;

  const background = isDraw
    ? "var(--cn-alkagi-surface-secondary)"
    : seat
      ? seatTokenColor(seat)
      : "var(--cn-alkagi-surface-secondary)";
  const color = isDraw
    ? "var(--cn-alkagi-text)"
    : seat
      ? seatTextColor(seat)
      : "var(--cn-alkagi-text)";

  const text = isDraw
    ? "무승부"
    : myParticipantId === null
      ? `${seat ? seatLabel(seat) : "승자"} 승리`
      : isWin
        ? "승리!"
        : "패배";

  return (
    <div
      className="rounded-lg px-4 py-2 text-center"
      style={{
        background,
        color,
        border:
          seat === "white"
            ? "1px solid var(--cn-alkagi-stone-white-border)"
            : "1px solid transparent",
      }}
      data-testid="alkagi-win-banner"
    >
      <span className="font-bold text-base">{text}</span>
      {gameState.outcomeReason && (
        <span className="ml-2 font-normal text-xs opacity-80">
          ({gameState.outcomeReason})
        </span>
      )}
    </div>
  );
}

export function MultiplayerGame() {
  const { user } = useAuth();
  const { currentRoom } = useLobby();
  const lobbyActions = useLobbyActions();
  const mpState = useMultiplayerGame();
  const mpActions = useMultiplayerGameActions();
  const roomId = currentRoom?.id ?? null;

  const isHostUser = currentRoom?.hostUserId === user?.id;
  const isRoomObserver =
    user?.id !== undefined &&
    (currentRoom?.observerUserIds ?? []).includes(user.id) &&
    !(currentRoom?.participantUserIds ?? []).includes(user.id);
  const isMyTurn =
    !isRoomObserver &&
    mpState.gameState?.currentParticipant === mpState.myParticipantId;
  const isGameOver = mpState.gameState?.phase === "ENDED";

  const canvasSize = useCanvasSize(
    mpState.resolvedInitConfig.coreConfig.space.cols,
    mpState.resolvedInitConfig.coreConfig.space.rows,
  );

  const mpActionsRef = useRef(mpActions);
  const lobbyActionsRef = useRef(lobbyActions);
  useEffect(() => {
    mpActionsRef.current = mpActions;
    lobbyActionsRef.current = lobbyActions;
  }, [mpActions, lobbyActions]);

  // Initialize game state on mount
  // biome-ignore lint/correctness/useExhaustiveDependencies: using primitive deps to avoid re-firing on every WS room update
  useEffect(() => {
    if (!roomId || !currentRoom || !user) return;

    if (isRoomObserver) {
      void multiplayerGameActions.initializeAsObserver(roomId);
    } else if (isHostUser) {
      void multiplayerGameActions.initializeAsHost(roomId);
    } else {
      multiplayerGameActions.initializeAsGuest(roomId);
      void multiplayerGameActions.replayEvents(roomId);
    }
  }, [roomId, currentRoom?.id, user?.id, isHostUser, isRoomObserver]);

  // Real-time game events via WebSocket
  const { connectionStatus } = useGameEventsWS(roomId, {
    onGameEvent: (eventType, payload, seq) => {
      mpActionsRef.current.applyRemoteEvent(eventType, payload, seq);
    },
    onRoomUpdated: (room) => {
      lobbyActionsRef.current.updateCurrentRoom(room);
    },
    onReconnect: () => {
      if (roomId) void multiplayerGameActions.replayEvents(roomId);
    },
  });

  const handleFlick = useCallback(
    async (entityId: string, impulseX: number, impulseY: number) => {
      if (!isMyTurn || isGameOver || mpState.isAnimating) return;
      if (!mpState.myParticipantId) return;

      await mpActions.submitAction({
        type: "APPLY_IMPULSE",
        participantId: mpState.myParticipantId,
        entityId,
        impulse: { x: impulseX, y: impulseY },
      });
    },
    [
      isMyTurn,
      isGameOver,
      mpState.isAnimating,
      mpState.myParticipantId,
      mpActions,
    ],
  );

  const handleAnimationComplete = useCallback(() => {
    mpActions.finishAnimation();
  }, [mpActions]);

  const handleConcede = async () => {
    await mpActions.submitConcede();
  };

  const handleLeaveGame = () => {
    mpActions.reset();
    lobbyActions.leaveGame();
    navigateTo(buildLobbyPath("alkagi", resolveAlkagiRouteMode()));
  };

  if (!currentRoom || !mpState.gameState) {
    return (
      <div
        className="flex min-h-dvh items-center justify-center"
        style={{
          background: "var(--cn-alkagi-surface-secondary)",
          color: "var(--cn-alkagi-text)",
        }}
      >
        {"불러오는 중..."}
      </div>
    );
  }

  const selectedEntity = mpState.selectedEntityId
    ? (mpState.gameState.entities.find(
        (entity) => entity.id === mpState.selectedEntityId,
      ) ?? null)
    : null;

  const userLabel = getUserLabel(user);
  const myParticipantIndex = mpState.gameState.participants.findIndex(
    (p) => p.id === mpState.myParticipantId,
  );
  const mySeat: SeatLabel | null =
    myParticipantIndex >= 0 ? seatFor(myParticipantIndex) : null;
  const roleLabel = isRoomObserver
    ? "Observer (관전)"
    : `${seatLabel(mySeat ?? "white")} (${isHostUser ? "호스트" : "게스트"})`;

  const playerOne = mpState.gameState.participants[0];
  const playerTwo = mpState.gameState.participants[1];
  const whiteCount = countRemaining(mpState.gameState.entities, playerOne.id);
  const blackCount = playerTwo
    ? countRemaining(mpState.gameState.entities, playerTwo.id)
    : 0;

  const activeIndex = mpState.gameState.participants.findIndex(
    (p) => p.id === mpState.gameState?.currentParticipant,
  );
  const activeSeat: SeatLabel =
    activeIndex >= 0 ? seatFor(activeIndex) : "white";

  return (
    <div
      data-testid="game-screen"
      data-game="alkagi"
      className="flex min-h-dvh flex-col items-center p-4 landscape:h-dvh landscape:overflow-hidden landscape:p-2"
      style={{
        background: "var(--cn-alkagi-surface-secondary)",
        color: "var(--cn-alkagi-text)",
      }}
    >
      <div className="mx-auto w-full max-w-3xl space-y-3">
        {/* Top bar */}
        <header
          className="flex flex-wrap items-center justify-between gap-2 rounded-xl px-4 py-2.5"
          style={{
            background: "var(--cn-alkagi-surface)",
            border: "1px solid var(--cn-alkagi-surface-border)",
          }}
          data-testid="alkagi-top-bar"
        >
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleLeaveGame}
              className="rounded-lg px-3 py-1.5 font-medium text-sm"
              style={{
                background: "transparent",
                border: "1px solid var(--cn-alkagi-surface-border)",
                color: "var(--cn-alkagi-text)",
                cursor: "pointer",
              }}
              data-testid="alkagi-new-game"
            >
              {"← 나가기"}
            </button>
            {!isGameOver && (
              <ActivePlayerChip seat={activeSeat} isMyTurn={isMyTurn} />
            )}
          </div>
          <div className="flex items-center gap-2">
            <ScoreChip
              seat="white"
              count={whiteCount}
              isActive={!isGameOver && activeSeat === "white"}
            />
            <span
              className="font-mono text-xs"
              style={{ color: "var(--cn-alkagi-text-muted)" }}
              data-testid="alkagi-turn-badge"
            >
              T{mpState.gameState.turn}
            </span>
            <ScoreChip
              seat="black"
              count={blackCount}
              isActive={!isGameOver && activeSeat === "black"}
            />
            <ConnectionPill
              connectionStatus={connectionStatus}
              userLabel={userLabel}
              roleLabel={roleLabel}
            />
          </div>
        </header>

        {isGameOver && (
          <WinBanner
            gameState={mpState.gameState}
            myParticipantId={mpState.myParticipantId}
          />
        )}

        {isRoomObserver && (
          <div
            data-testid="observer-mode-banner"
            className="rounded-md px-4 py-2 text-center font-semibold text-sm"
            style={{
              background: "var(--cn-alkagi-surface)",
              border: "1px solid var(--cn-alkagi-surface-border)",
              color: "var(--cn-alkagi-text-muted)",
            }}
          >
            관전 모드 · 읽기 전용
          </div>
        )}

        {mpState.lastError && (
          <div
            className="rounded-md px-4 py-2 text-sm"
            style={{
              background: "var(--cn-alkagi-error-bg)",
              color: "var(--cn-alkagi-error)",
              border: "1px solid var(--cn-alkagi-error)",
            }}
          >
            {mpState.lastError}
          </div>
        )}

        {/* Board frame */}
        <div
          className="rounded-2xl p-3"
          style={{
            background: "var(--cn-alkagi-board-fill)",
            border: "8px solid var(--cn-alkagi-board-frame)",
          }}
          data-testid="alkagi-board-frame"
        >
          <div className="flex items-center justify-center">
            <div className="alkagi-board-container relative">
              <GameCanvas
                entities={mpState.gameState.entities}
                selectedEntityId={mpState.selectedEntityId}
                isAnimating={mpState.isAnimating}
                animationRecording={mpState.animationRecording}
                currentParticipant={mpState.gameState.currentParticipant}
                isRunning={!isGameOver && isMyTurn}
                canvasWidth={canvasSize.width}
                canvasHeight={canvasSize.height}
                canvasPadding={canvasSize.padding}
                pieceTypesById={mpState.resolvedInitConfig.pieceTypesById}
                boardCols={mpState.resolvedInitConfig.coreConfig.space.cols}
                boardRows={mpState.resolvedInitConfig.coreConfig.space.rows}
                ownerOneId={playerOne.id}
                onSelectEntity={mpActions.selectEntity}
                onFlick={handleFlick}
                onAnimationComplete={handleAnimationComplete}
              />
              {isGameOver && (
                <GameOverScreen
                  gameState={mpState.gameState}
                  isMobile={canvasSize.isMobile}
                  onNewGame={handleLeaveGame}
                />
              )}
            </div>
          </div>
        </div>

        {/* Per-player remaining-stone counters */}
        <div
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg px-4 py-2"
          style={{
            background: "var(--cn-alkagi-surface)",
            border: "1px solid var(--cn-alkagi-surface-border)",
          }}
          data-testid="alkagi-stone-counters"
        >
          <StoneCounterRow seat="white" count={whiteCount} />
          <StoneCounterRow seat="black" count={blackCount} />
        </div>

        <GameInfo
          gameState={mpState.gameState}
          selectedEntity={selectedEntity}
          pieceTypesById={mpState.resolvedInitConfig.pieceTypesById}
          isAnimating={mpState.isAnimating}
          lastError={mpState.lastError}
          isMobile={canvasSize.isMobile}
          canConcede={!isRoomObserver}
          onConcede={handleConcede}
          onNewGame={handleLeaveGame}
        />

        {/* Controls */}
        <div className="flex w-full items-center justify-center gap-3">
          {!isGameOver && !isRoomObserver && (
            <button
              type="button"
              data-testid="resign-btn"
              onClick={handleConcede}
              className="cursor-pointer rounded-md border-none px-5 py-2.5 font-bold text-sm"
              style={{
                background: "var(--cn-alkagi-error)",
                color: "var(--cn-alkagi-surface)",
              }}
            >
              {"기권"}
            </button>
          )}
          <button
            type="button"
            data-testid="leave-game-btn"
            onClick={handleLeaveGame}
            className="cursor-pointer rounded-md border-none px-5 py-2.5 font-bold text-sm"
            style={{
              background: "var(--cn-alkagi-surface-secondary)",
              color: "var(--cn-alkagi-text)",
              border: "1px solid var(--cn-alkagi-surface-border)",
            }}
          >
            {"나가기"}
          </button>
        </div>
      </div>
    </div>
  );
}
